import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { RangeSliderComponent } from '../range-slider/range-slider.component';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { RecorderStore } from '../../state/recorder.store';
import { ThemeService, liftToLight } from '../../services/theme.service';

type Tone = 'off' | 'warm' | 'bright' | 'cool';

const TONE_PRESETS: Record<
  Tone,
  { toneOn: boolean; warmth: number; tooltip: string }
> = {
  off:    { toneOn: false, warmth: 50, tooltip: 'No colored fill — Brightness shifts the theme background' },
  warm:   { toneOn: true,  warmth: 80, tooltip: 'Warm, golden fill light — flattering and natural' },
  bright: { toneOn: true,  warmth: 50, tooltip: 'Bright, true-white fill light — clean and even' },
  cool:   { toneOn: true,  warmth: 20, tooltip: 'Cool, daylight-blue fill light — crisp and modern' },
};

/**
 * ScreenFillLightControlComponent — four tone buttons (Off/Warm/Bright/Cool)
 * + a Brightness slider. No on/off toggle, no advanced expander, no warmth
 * slider. "Tone Off" preserves the existing theme background; brightness
 * slides the overlay opacity from invisible (0) → fully opaque (100).
 */
@Component({
  selector: 'rec-fill-light',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RangeSliderComponent, TooltipDirective],
  templateUrl: './fill-light.component.html',
  styleUrls: ['./fill-light.component.scss'],
})
export class FillLightComponent {
  readonly store = inject(RecorderStore);
  readonly themeService = inject(ThemeService);

  /** Which part(s) to render. Lets the parent place tones and brightness
   *  in separate spots in the Lighting section. */
  readonly show = input<'all' | 'tones' | 'brightness'>('all');

  /** Fired when the user moves the brightness slider directly — lets the
   *  parent component clear any preset (e.g. Sun Mode) the user is overriding. */
  readonly userBrightnessChange = output<number>();

  /** Ordered Natural → Cool → Bright → Warm so the slider reads left-to-right. */
  readonly tones: { name: Tone; label: string; tooltip: string }[] = [
    { name: 'off',    label: 'Natural', tooltip: TONE_PRESETS.off.tooltip },
    { name: 'cool',   label: 'Cool',    tooltip: TONE_PRESETS.cool.tooltip },
    { name: 'bright', label: 'Bright',  tooltip: TONE_PRESETS.bright.tooltip },
    { name: 'warm',   label: 'Warm',    tooltip: TONE_PRESETS.warm.tooltip },
  ];

  readonly brightness = computed(() => this.store.fillLight().brightness);

  readonly activeTone = computed<Tone>(() => {
    const fl = this.store.fillLight();
    if (!fl.toneOn) return 'off';
    if (fl.warmth === TONE_PRESETS.warm.warmth)   return 'warm';
    if (fl.warmth === TONE_PRESETS.cool.warmth)   return 'cool';
    return 'bright';
  });

  readonly activeIndex = computed(() => {
    const active = this.activeTone();
    return this.tones.findIndex(t => t.name === active);
  });

  /**
   * Brightness slider track gradient — paints the slider with the active
   * theme's dark→lit color so users can preview the extremes before they
   * drag. Re-evaluates on theme change via themeVersion.
   */
  readonly brightnessGradient = computed(() => {
    this.themeService.themeVersion();
    const theme = this.themeService.activeTheme;
    const themed = theme.fillLight;
    let dr: number, dg: number, db: number;
    let lr: number, lg: number, lb: number;

    if (themed) {
      [dr, dg, db] = themed.dark;
      [lr, lg, lb] = themed.light;
    } else {
      const shellBg = theme.tokens['--shell-bg'] ?? '';
      const hex = shellBg.match(/#[0-9a-fA-F]{6}/)?.[0] ?? '#04071c';
      dr = parseInt(hex.slice(1, 3), 16);
      dg = parseInt(hex.slice(3, 5), 16);
      db = parseInt(hex.slice(5, 7), 16);
      [lr, lg, lb] = liftToLight(hex);
    }
    return `linear-gradient(90deg, rgb(${dr},${dg},${db}) 0%, rgb(${lr},${lg},${lb}) 100%)`;
  });

  /**
   * Tracks whether the current brightness value came from an auto-bump
   * (colored tone selected while brightness was 0). Cleared as soon as the
   * user manually moves the brightness slider — that hands ownership of
   * brightness back to the user, so switching to Natural won't revert.
   */
  private _autoBumpedFromZero = false;

  applyTone(tone: Tone): void {
    const p = TONE_PRESETS[tone];
    this.store.setToneOn(p.toneOn);
    this.store.setWarmth(p.warmth);

    if (p.toneOn) {
      // Auto-bump brightness when the user picks a colored tone but
      // brightness is at 0 — otherwise the choice would have no visible
      // effect and feel broken.
      if (this.store.fillLight().brightness < 10) {
        this.store.setBrightness(25);
        this._autoBumpedFromZero = true;
      }
    } else if (this._autoBumpedFromZero) {
      // Returning to Natural and the only brightness change so far came
      // from the auto-bump — restore to the original 0.
      this.store.setBrightness(0);
      this._autoBumpedFromZero = false;
    }
  }

  onToneIndex(i: number): void {
    const t = this.tones[i];
    if (t) this.applyTone(t.name);
  }

  onBrightness(v: number): void {
    // User manually moving the slider takes ownership of brightness —
    // Natural no longer reverts to 0.
    this._autoBumpedFromZero = false;
    this.store.setBrightness(v);
    this.userBrightnessChange.emit(v);
  }
}
